/**
 * Terms of Service — public static page.
 */
export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <a
          href="/"
          className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] hover:text-[#E05A1D] transition-colors mb-8"
          data-testid="terms-back-link"
        >
          ← Back to UpTop
        </a>

        <h1
          className="text-4xl sm:text-5xl font-black tracking-tighter uppercase mb-2"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          <span className="text-[#E05A1D]">UP</span>TOP
        </h1>
        <h2 className="text-2xl font-bold tracking-tight mb-8 text-white/90">Terms of Service</h2>
        <p className="text-xs text-[#71717A] uppercase tracking-widest mb-12">
          Last updated: February 2026
        </p>

        <Section title="1. Acceptance">
          <p>
            By installing or using UpTop you agree to these Terms. If you don't agree, please
            stop using the app and delete your account.
          </p>
        </Section>

        <Section title="2. The service">
          <p>
            UpTop is a personal fitness tracking app. We provide it on an "as is" and "as
            available" basis. We may add, change, or remove features at any time without prior
            notice. We may also suspend or end the service if running it becomes impractical.
          </p>
        </Section>

        <Section title="3. Not medical advice">
          <p>
            UpTop is a tracking and coaching tool. Workout plans, calorie targets, macro
            splits and AI suggestions are general guidance based on standard fitness
            formulas and large-language-model output. They are <strong>not</strong> medical,
            dietary, or psychological advice and should not replace consultation with a
            qualified professional. Always check with your doctor before starting a new
            training program, dietary regime, or if you have a pre-existing condition.
            You exercise at your own risk.
          </p>
        </Section>

        <Section title="4. Your account">
          <p>
            You're responsible for the accuracy of the data you enter and for keeping any
            linked email address secure. If you let someone else use your device, they may
            see and modify your tracked data.
          </p>
        </Section>

        <Section title="5. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Reverse-engineer, scrape, or attempt to abuse the API.</li>
            <li>Upload content that is illegal, harassing, or infringes anyone's rights.</li>
            <li>Use the AI features to generate harmful, deceptive, or unlawful content.</li>
            <li>Interfere with the operation of the service or other users.</li>
          </ul>
        </Section>

        <Section title="6. Intellectual property">
          <p>
            The UpTop name, logo, screens, copy and source code are owned by the developer.
            You own the data you enter. By using the AI features you grant us a temporary,
            limited license to process that data to deliver a response back to you.
          </p>
        </Section>

        <Section title="7. Termination">
          <p>
            You may delete your account at any time from Settings → Delete Account. We may
            terminate your access if you breach these Terms. Once deleted, your data is
            permanently removed from our database.
          </p>
        </Section>

        <Section title="8. Limitation of liability">
          <p>
            To the maximum extent permitted by law, UpTop and its operators are not liable
            for any indirect, incidental, special, consequential, or punitive damages, or
            for loss of data, lost profits, or business interruption arising from your use of
            the app. Our total liability for any claim is limited to the greater of (a) the
            amount you have paid us in the last twelve months or (b) USD 10.
          </p>
        </Section>

        <Section title="9. Changes">
          <p>
            We may update these Terms from time to time. Significant changes will be
            announced inside the app or via email. Continued use after the change means you
            accept the new Terms.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Email{" "}
            <a
              href="mailto:hello@uptop.app"
              className="text-[#E05A1D] hover:underline"
            >
              hello@uptop.app
            </a>{" "}
            for any question about these Terms.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#27272A] text-xs text-[#71717A]">
          See also our{" "}
          <a href="/privacy" className="text-[#E05A1D] hover:underline">
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h3
        className="text-lg font-bold tracking-tight uppercase mb-3 text-white"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        {title}
      </h3>
      <div className="text-sm leading-relaxed text-[#A1A1AA] space-y-2">{children}</div>
    </section>
  );
}
